import { useCallback, useMemo, useState } from 'react';
import axios, { AxiosProgressEvent } from 'axios';
import { ApolloError } from '@apollo/client';

import { snackbar } from '@occc-app/design-system';
import { Segment_Upload_Mode } from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';

import {
  useGenerateSegmentUploadSignedUrl,
  useProcessSegmentUpload,
} from './segments.mutation.hook';

import { SEGMENT_UPLOAD_FILE_SIZE_ERROR } from '../segments.constants';
import { UploadedFileState } from '../segments.types';

export type UseSegmentUploadFileControllerParams = {
  segmentUuid: string;
};

const isEntityTooLargeError = (error: unknown) =>
  axios.isAxiosError(error) &&
  typeof error.response?.data === 'string' &&
  error.response.data.includes('EntityTooLarge');

const showErrorMessage = (error?: unknown) => {
  if (isEntityTooLargeError(error)) {
    snackbar.error(SEGMENT_UPLOAD_FILE_SIZE_ERROR);
    return;
  }

  const message = error
    ? getErrorMessage(error as ApolloError)
    : 'An error occurred while uploading the file';
  snackbar.error(message);
};

export const useSegmentUploadFileController = ({
  segmentUuid,
}: UseSegmentUploadFileControllerParams) => {
  const [uploadedFileState, setUploadedFileState] = useState<UploadedFileState | null>(null);

  const [generateSignedUrl] = useGenerateSegmentUploadSignedUrl();
  const [processSegmentUpload] = useProcessSegmentUpload({
    refetchQueries: ['getSegmentUploadedFiles'],
  });

  const uploadFileToSignedUrlWithProgress = useCallback(
    async (file: File, signedUrl: string, fields: object) => {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      return axios.post(signedUrl, formData, {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const progress = progressEvent.loaded / progressEvent.total;

            setUploadedFileState((fileStatus) => {
              return fileStatus
                ? {
                    ...fileStatus,
                    progress,
                  }
                : null;
            });
          }
        },
      });
    },
    [],
  );

  const uploadFile = useCallback(
    async (selectedFile: File, mode: Segment_Upload_Mode) => {
      setUploadedFileState({
        fileName: selectedFile.name,
        progress: 0,
      });

      try {
        const { data } = await generateSignedUrl({
          variables: { fileName: selectedFile.name },
        });
        if (!data) {
          showErrorMessage();
          return;
        }

        const { signedUrl, fileUuid, fields } = data.generateSegmentUploadSignedUrl;

        if (Array.isArray(fields)) {
          showErrorMessage();
          return;
        }

        await uploadFileToSignedUrlWithProgress(selectedFile, signedUrl, fields);

        const processSegmentUploadResponse = await processSegmentUpload({
          variables: {
            input: {
              fileUuid,
              segmentUuid,
              mode,
            },
          },
        });

        if (!processSegmentUploadResponse.data?.processSegmentUpload) {
          showErrorMessage();
          return;
        }
      } catch (error) {
        showErrorMessage(error);
      } finally {
        setUploadedFileState(null);
      }
    },
    [generateSignedUrl, processSegmentUpload, segmentUuid, uploadFileToSignedUrlWithProgress],
  );

  const state = useMemo(
    () => ({
      uploadedFileState,
      uploadFile,
    }),
    [uploadedFileState, uploadFile],
  );

  return state;
};
